import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send } from "lucide-react";

// TODO: Reemplazar por la URL real del webhook n8n cuando esté lista.
const SUPPORT_WEBHOOK_URL = "";

type TicketType = "bug" | "duda" | "feature";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportModal({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [ticketType, setTicketType] = useState<TicketType>("duda");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setTicketType("duda");
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({
        title: "Mensaje vacío",
        description: "Cuéntanos brevemente qué necesitas.",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      if (SUPPORT_WEBHOOK_URL) {
        await fetch(SUPPORT_WEBHOOK_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: ticketType,
            message: message.trim(),
            user_id: user?.id ?? null,
            user_email: user?.email ?? null,
            sent_at: new Date().toISOString(),
            url: window.location.href,
            user_agent: navigator.userAgent,
          }),
        });
      }

      toast({
        title: "Mensaje enviado",
        description: "Nuestro equipo te contactará lo antes posible.",
      });
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: "No pudimos enviar tu mensaje",
        description: err?.message ?? "Inténtalo de nuevo en unos minutos.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ayuda y Soporte</DialogTitle>
          <DialogDescription>
            Escríbenos y un humano del equipo Kargonode te responderá.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-type">Tipo de solicitud</Label>
            <Select
              value={ticketType}
              onValueChange={(v) => setTicketType(v as TicketType)}
            >
              <SelectTrigger id="ticket-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">🐛 Reportar un bug</SelectItem>
                <SelectItem value="duda">❓ Tengo una duda</SelectItem>
                <SelectItem value="feature">✨ Sugerir mejora</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ticket-message">Mensaje</Label>
            <Textarea
              id="ticket-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe brevemente lo que necesitas…"
              rows={5}
              maxLength={2000}
            />
            <p className="text-[11px] text-muted-foreground text-right tabular-nums">
              {message.length} / 2000
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={sending || !message.trim()}>
            {sending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="mr-2 h-3.5 w-3.5" />
            )}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
